import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { useAddEventMutation } from "src/api/purchasing/events";
import { useGetPaymentMethods } from "src/api/generic";
import { useTranslation } from "react-i18next";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";
import { useGetSalaryEmployees } from "src/api/salary/employees";

interface Props {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
}

interface EmployeeRow {
  employee_id: string;
  amount: string;
}

const EmployeeEventPopup = ({ open, handleClose, refetch }: Props) => {
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;

  const [employeeRows, setEmployeeRows] = useState<EmployeeRow[]>([
    { employee_id: "", amount: "" },
  ]);
  const [employeeErrors, setEmployeeErrors] = useState<
    Record<number, { employee_id?: string; amount?: string }>
  >({});

  const schema = yup.object({
    direction: yup
      .string()
      .oneOf(["to_employee", "from_employee"])
      .required(t("required")),
    method: yup.string().when("direction", {
      is: "to_employee",
      then: (v) =>
        v.oneOf(["employee_loan", "bulk_transfer"]).required(t("required")),
      otherwise: (v) => v.notRequired(),
    }),
    payment_id: yup.string().when("direction", {
      is: (direction: string) => !!direction,
      then: (v) => v.required(t("required")),
      otherwise: (v) => v.notRequired(),
    }),
    amount: yup.number().when("direction", {
      is: (direction: string) => !!direction,
      then: (v) => v.required(t("required")).min(0, t("required")),
      otherwise: (v) => v.notRequired(),
    }),
    date: yup.date().required(t("required")),
    note: yup.string(),
  });

  type FormInputs = yup.InferType<typeof schema>;

  const { mutate, isPending } = useAddEventMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: employeesData } = useGetSalaryEmployees({ tab: "active" });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const direction = watch("direction");
  const method = watch("method");

  const isBulkTransfer =
    direction === "to_employee" && method === "bulk_transfer";

  const handleAddEmployee = () => {
    setEmployeeRows((prev) => [...prev, { employee_id: "", amount: "" }]);
  };

  const handleRemoveEmployee = (index: number) => {
    setEmployeeRows((prev) => prev.filter((_, i) => i !== index));
    setEmployeeErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleEmployeeChange = (
    index: number,
    field: keyof EmployeeRow,
    value: string,
  ) => {
    setEmployeeRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setEmployeeErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: undefined },
    }));
  };

  const validateEmployees = (): boolean => {
    const errs: Record<number, { employee_id?: string; amount?: string }> = {};
    let valid = true;
    employeeRows.forEach((row, i) => {
      if (!row.employee_id) {
        errs[i] = { ...errs[i], employee_id: t("required") };
        valid = false;
      }
      if (!row.amount || Number(row.amount) < 0) {
        errs[i] = { ...errs[i], amount: t("required") };
        valid = false;
      }
    });
    setEmployeeErrors(errs);
    return valid;
  };

  const onClose = () => {
    handleClose();
    reset({});
    setEmployeeRows([{ employee_id: "", amount: "" }]);
    setEmployeeErrors({});
    setFiles([]);
  };

  const onSubmit = (data: FormInputs) => {
    if (isBulkTransfer) {
      if (!validateEmployees()) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: t("employeeFieldsRequired"),
          },
        ]);
        return;
      }

      const employeesSum = employeeRows.reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0,
      );

      if (Number(data.amount) !== employeesSum) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: t("employeeAmountsMismatch"),
          },
        ]);
        return;
      }
    } else {
      if (!employeeRows[0]?.employee_id) {
        setEmployeeErrors({ 0: { employee_id: t("required") } });
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: t("employeeFieldsRequired"),
          },
        ]);
        return;
      }
    }

    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${company?.start_balance_date}`,
        },
      ]);
      return;
    }

    const formData = new FormData();
    formData.append("type", "employee");
    formData.append("operation", data.direction);
    formData.append("date", moment(data.date).format("YYYY-MM-DD"));
    formData.append("amount", String(data.amount));

    if (data.direction === "to_employee") {
      formData.append("method", data.method!);
      formData.append("payment_from_id", data.payment_id!);
    } else {
      formData.append("method", "payment");
      formData.append("payment_to_id", data.payment_id!);
    }

    if (isBulkTransfer) {
      employeeRows.forEach((row, index) => {
        formData.append(`employees[${index}][employee_id]`, row.employee_id);
        formData.append(`employees[${index}][amount]`, String(row.amount));
      });
    } else {
      formData.append("employees[0][employee_id]", employeeRows[0].employee_id);
      formData.append("employees[0][amount]", String(data.amount));
    }

    if (data.note) {
      formData.append("note", data.note);
    }

    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message:
              error?.data?.errors?.[0].value || generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  const selectedEmployeeIds = employeeRows.map((r) => r.employee_id);

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", lg: "537px" },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography color="#101828" fontSize="20px" lineHeight="24px">
            {t("addEvent")}
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            {/* Event Type (read-only) */}
            <FormControl fullWidth size="small" disabled>
              <InputLabel>{t("eventType")}</InputLabel>
              <Select value="employee" label={t("eventType")}>
                <MenuItem value="employee">{t("employeeEvent")}</MenuItem>
              </Select>
            </FormControl>

            {/* Direction */}
            <Box>
              <Typography fontSize="14px" color="#101828" mb={0.5}>
                {t("type")}
              </Typography>
              <FormControl size="small" error={!!errors.direction}>
                <Controller
                  name="direction"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      row
                      onChange={(e) => {
                        field.onChange(e);
                        setValue("method", undefined);
                        setValue("payment_id", undefined);
                        setEmployeeRows([{ employee_id: "", amount: "" }]);
                        setEmployeeErrors({});
                      }}
                    >
                      <FormControlLabel
                        value="to_employee"
                        control={<Radio size="small" />}
                        label={t("toEmployee")}
                      />
                      <FormControlLabel
                        value="from_employee"
                        control={<Radio size="small" />}
                        label={t("fromEmployee")}
                      />
                    </RadioGroup>
                  )}
                />
                <FormHelperText>{errors.direction?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* Transfer Type radio - To Employee */}
            {direction === "to_employee" && (
              <Box>
                <Typography fontSize="14px" color="#101828" mb={0.5}>
                  {t("transferType")}
                </Typography>
                <FormControl size="small" error={!!errors.method}>
                  <Controller
                    name="method"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value === "employee_loan") {
                            setEmployeeRows((prev) => [prev[0]]);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="employee_loan"
                          control={<Radio size="small" />}
                          label={t("employeeLoan")}
                        />
                        <FormControlLabel
                          value="bulk_transfer"
                          control={<Radio size="small" />}
                          label={t("bulkTransfer")}
                        />
                      </RadioGroup>
                    )}
                  />
                  <FormHelperText>{errors.method?.message}</FormHelperText>
                </FormControl>
              </Box>
            )}

            {/* Employee rows */}
            {direction &&
              employeeRows.map((row, index) => (
                <Stack
                  key={index}
                  direction="row"
                  gap={1}
                  alignItems="flex-start"
                >
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!employeeErrors[index]?.employee_id}
                  >
                    <InputLabel>
                      {isBulkTransfer
                        ? `${generalT("employee")} ${index + 1}`
                        : generalT("employee")}
                    </InputLabel>
                    <Select
                      value={row.employee_id}
                      onChange={(e) =>
                        handleEmployeeChange(
                          index,
                          "employee_id",
                          e.target.value as string,
                        )
                      }
                      label={
                        isBulkTransfer
                          ? `${generalT("employee")} ${index + 1}`
                          : generalT("employee")
                      }
                    >
                      {employeesData?.data
                        ?.filter(
                          (emp: any) =>
                            !selectedEmployeeIds.includes(String(emp.id)) ||
                            String(emp.id) === row.employee_id,
                        )
                        .map((emp: any) => (
                          <MenuItem key={emp.id} value={String(emp.id)}>
                            {emp.name}
                          </MenuItem>
                        ))}
                      {employeesData?.data?.length === 0 && (
                        <Typography align="center" color="textSecondary">
                          {generalT("noData")}
                        </Typography>
                      )}
                    </Select>
                    <FormHelperText>
                      {employeeErrors[index]?.employee_id}
                    </FormHelperText>
                  </FormControl>

                  {isBulkTransfer && (
                    <TextField
                      type="number"
                      size="small"
                      label={t("amount")}
                      value={row.amount}
                      onChange={(e) =>
                        handleEmployeeChange(index, "amount", e.target.value)
                      }
                      error={!!employeeErrors[index]?.amount}
                      helperText={employeeErrors[index]?.amount}
                      sx={{ minWidth: 140 }}
                    />
                  )}
                  {isBulkTransfer && employeeRows.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveEmployee(index)}
                      sx={{ mt: 0.5 }}
                    >
                      <img src={DeleteIcon} alt="" width="16px" />
                    </IconButton>
                  )}
                </Stack>
              ))}

            {/* Add more employee button */}
            {isBulkTransfer && (
              <Button
                variant="text"
                size="small"
                startIcon={<img src={AddMoreIcon} alt="" />}
                onClick={handleAddEmployee}
                sx={{ alignSelf: "flex-start" }}
              >
                {t("addMoreEmployee")}
              </Button>
            )}

            {/* Payment dropdown */}
            {direction && (
              <FormControl fullWidth size="small" error={!!errors.payment_id}>
                <InputLabel>{t("payment")}</InputLabel>
                <Controller
                  name="payment_id"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label={t("payment")}>
                      {paymentsData?.data
                        ?.filter((pay: any) => pay.status)
                        .map((pay: any) => (
                          <MenuItem key={pay.id} value={String(pay.id)}>
                            {pay.name}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.payment_id?.message}</FormHelperText>
              </FormControl>
            )}

            {/* Event Amount */}
            {direction && (
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    label={t("amount")}
                    fullWidth
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                  />
                )}
              />
            )}

            {/* Note */}
            {direction && (
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label={t("notesOptional")}
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            )}

            {/* Date */}
            {direction && (
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    format="dd/MM/yyyy"
                    label={t("transactionDateRepeat")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!errors.date,
                        helperText: errors.date?.message,
                        inputProps: {
                          readOnly: true,
                          onKeyDownCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                          onPasteCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        },
                      },
                    }}
                  />
                )}
              />
            )}

            {/* File Upload */}
            {direction && (
              <>
                <Stack
                  direction="row"
                  spacing="6px"
                  alignItems="center"
                  marginBottom={0}
                >
                  <FiberManualRecordIcon style={{ fontSize: "10px" }} />
                  <Typography
                    fontSize="16px"
                    lineHeight="19px"
                    color="#101828"
                  >
                    {t("uploadAttachment")}
                  </Typography>
                </Stack>
                <FilePondUploader
                  onUpload={(e) => setFiles(e)}
                  maxFiles={3}
                  maxFileSize={3}
                  acceptedFileTypes={["image/*", "application/pdf"]}
                />
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={2} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={onClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default EmployeeEventPopup;
